import { DEFAULT_REPOSITORY_INDEXING_MODE } from "@arti/shared-source-control/indexing/runMode";
import { useCallback, useEffect, useMemo, useState } from "react";

import type {
  RepositoryIndexingRun,
  RepositoryIndexingRunRepositoryModeSelection,
} from "../types";

import {
  createIndexingRunAsUser,
  listIndexingRunsAsUser,
} from "../dashboard-client";
import { formatErrorMessage } from "../utils/errors";
import { buildOptimisticRunPlaceholder } from "../utils/sourceControlIndexing";

interface RunsState {
  runs: RepositoryIndexingRun[];
  loading: boolean;
  error: string | null;
  triggering: boolean;
  selectedRunId: string | null;
}

const initialState: RunsState = {
  error: null,
  loading: false,
  runs: [],
  selectedRunId: null,
  triggering: false,
};

export function useRepositoryIndexingRuns() {
  const [state, setState] = useState<RunsState>(initialState);

  const refreshRuns = useCallback(async () => {
    setState((current) => ({ ...current, error: null, loading: true }));
    try {
      const listRunsResult = await listIndexingRunsAsUser();
      const { runs } = listRunsResult.data;
      setState((current) => ({
        ...current,
        loading: false,
        runs,
        selectedRunId: ensureSelectedRunId(current.selectedRunId, runs),
      }));
    } catch (error) {
      setState((current) => ({
        ...current,
        error: formatErrorMessage(error),
        loading: false,
      }));
    }
  }, []);

  useEffect(() => {
    void refreshRuns();
  }, [refreshRuns]);

  const triggerManualRun = useCallback(
    async (
      repositoryModes: RepositoryIndexingRunRepositoryModeSelection[],
    ): Promise<boolean> => {
      const placeholderId = `pending-${Date.now()}`;
      setState((current) => {
        if (current.triggering) {
          return current;
        }
        const placeholder = buildOptimisticRunPlaceholder(placeholderId);
        return {
          ...current,
          runs: [placeholder, ...current.runs],
          selectedRunId: placeholderId,
          triggering: true,
        };
      });

      try {
        const createRunResult = await createIndexingRunAsUser({
          body: {
            mode: DEFAULT_REPOSITORY_INDEXING_MODE,
            repositories: repositoryModes.map(
              (repository) => repository.repository_slug,
            ),
            repository_modes: repositoryModes,
            requested_by: "dashboard/manual",
          },
        });
        const { run } = createRunResult.data;
        setState((current) => {
          const runsWithoutPlaceholder = current.runs.filter(
            (candidate) => candidate.run_id !== placeholderId,
          );
          return {
            ...current,
            error: null,
            runs: [run, ...runsWithoutPlaceholder],
            selectedRunId: run.run_id,
            triggering: false,
          };
        });
        return true;
      } catch (error) {
        const message = formatErrorMessage(error);
        setState((current) => {
          const filtered = current.runs.filter(
            (candidate) => candidate.run_id !== placeholderId,
          );
          return {
            ...current,
            error: message,
            runs: filtered,
            selectedRunId: ensureSelectedRunId(null, filtered),
            triggering: false,
          };
        });
        return false;
      }
    },
    [],
  );

  const selectRun = useCallback((runId: string) => {
    setState((current) => ({ ...current, selectedRunId: runId }));
  }, []);

  const selectedRun = useMemo(
    () => state.runs.find((run) => run.run_id === state.selectedRunId) ?? null,
    [state.runs, state.selectedRunId],
  );

  return {
    error: state.error,
    loading: state.loading,
    refreshRuns,
    runs: state.runs,
    selectRun,
    selectedRun,
    selectedRunId: state.selectedRunId,
    triggerManualRun,
    triggering: state.triggering,
  };
}

function ensureSelectedRunId(
  candidate: string | null,
  runs: RepositoryIndexingRun[],
): string | null {
  if (candidate && runs.some((run) => run.run_id === candidate)) {
    return candidate;
  }
  return runs[0]?.run_id ?? null;
}
